"use client";
import { jsx } from "react/jsx-runtime";
import { format } from "date-fns/format";
import { isBefore } from "date-fns/isBefore";
import { useCallback, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import { cn } from "../../../utils/index.js";
import { HeatMapDayButton } from "../CustomComponents/HeatMapDayButton.js";
import { NextMonthButton, PreviousMonthButton } from "../CustomComponents/MonthButtons.js";
import { getHoveredDateRange, isDateRange, resetDates } from "../helpers/helpers.js";
import { useMobileMonthScrollObserver } from "../hooks/useMobileMonthScrollObserver.js";
import { useScrollToSelectedMonth } from "../hooks/useScrollToSelectedMonth.js";
import { DayMapProvider } from "../Providers/DayMapProvider.js";
import { calendarVariants } from "./Calendar.variants.js";
function Calendar({ mode = 'single', numberOfMonths = 1, captionLayout = 'label', selected: selectedDates, handleCalendarChange, min = 0, max, startMonth, endMonth, onMonthChange, className, isMobile, components: customComponents, dayMap, scrollToSelectedMonthOnOpen, ...restProps }) {
    const datesResetRef = useRef(false);
    const monthsContainerRef = useRef(null);
    const [hoveredDate, setHoveredDate] = useState(null);
    useScrollToSelectedMonth({
        containerRef: monthsContainerRef,
        scrollToSelectedMonthOnOpen,
        isMobile,
        numberOfMonths,
        startMonth,
        mode,
        selected: selectedDates,
        defaultMonth: restProps.defaultMonth
    });
    useMobileMonthScrollObserver({
        containerRef: monthsContainerRef,
        isMobile,
        onMonthChange,
        startMonth,
        numberOfMonths
    });
    const { variant = 'day' } = dayMap || {};
    const handleSelect = useCallback((dates, triggerDate)=>{
        if (!dates) return;
        let newSelectedDates = dates;
        if ('range' === mode) {
            const newResetDates = resetDates({
                triggerDate,
                datesResetRef,
                selectedDates: selectedDates
            });
            if (newResetDates) newSelectedDates = newResetDates;
        }
        handleCalendarChange(newSelectedDates, triggerDate);
    }, [
        handleCalendarChange,
        mode,
        selectedDates
    ]);
    const handleDayMouseEnter = useCallback((day)=>{
        if (isMobile) return;
        const isSelectingRange = 'range' === mode && selectedDates && isDateRange(selectedDates) && selectedDates.from && !selectedDates.to && !isBefore(day, selectedDates.from);
        if (isSelectingRange) setHoveredDate(day);
        else if (null !== hoveredDate) setHoveredDate(null);
    }, [
        hoveredDate,
        isMobile,
        mode,
        selectedDates
    ]);
    const styles = useMemo(()=>calendarVariants({
            isMobile,
            heatMapVariant: variant
        }), [
        isMobile,
        variant
    ]);
    const classNames = useMemo(()=>({
            root: cn(styles.root(), className),
            month_caption: styles.monthCaption(),
            button_previous: styles.buttonPrevious(),
            button_next: styles.buttonNext(),
            caption_label: styles.captionLabel(),
            nav: styles.nav(),
            months: styles.months(),
            month: styles.month(),
            weekdays: styles.weekdays(),
            week_row: styles.weekRow(),
            week_header: styles.weekHeader(),
            day: styles.day(),
            selected: styles.selectedDays(),
            today: styles.today(),
            outside: styles.outsideDay(),
            weeks: styles.weeks(),
            week: styles.week(),
            weekday: styles.weekday(),
            week_number: styles.weekNumber(),
            chevron: styles.chevron(),
            month_grid: styles.monthGrid(),
            range_start: styles.rangeStart(),
            range_middle: styles.rangeMiddle(),
            range_end: styles.rangeEnd(),
            disabled: styles.disabled(),
            hidden: styles.hidden()
        }), [
        styles,
        className
    ]);
    const formatters = useMemo(()=>({
            formatWeekdayName: (weekday)=>format(weekday, 'ccc')
        }), []);
    const components = useMemo(()=>({
            NextMonthButton: NextMonthButton,
            PreviousMonthButton: PreviousMonthButton,
            DayButton: HeatMapDayButton,
            ...customComponents
        }), [
        customComponents
    ]);
    const modifiers = useMemo(()=>{
        if (isMobile) return;
        return getHoveredDateRange({
            mode,
            selectedDates: selectedDates,
            hoveredDate
        });
    }, [
        hoveredDate,
        mode,
        selectedDates,
        isMobile
    ]);
    const modifiersClassNames = useMemo(()=>{
        if (isMobile) return {};
        return {
            hoveredRangeStart: cn(styles.selectedDays(), styles.rangeStart()),
            hoveredRange: cn(styles.selectedDays(), styles.rangeMiddle()),
            hoveredRangeEnd: cn(styles.selectedDays(), styles.rangeEnd())
        };
    }, [
        styles,
        isMobile
    ]);
    const dayPickerProps = {
        ...restProps,
        classNames,
        mode,
        formatters,
        min,
        max,
        numberOfMonths,
        startMonth,
        endMonth,
        captionLayout,
        onMonthChange,
        components,
        fixedWeeks: !isMobile,
        selected: selectedDates,
        onSelect: handleSelect,
        hideNavigation: !!isMobile,
        required: 'single' === mode,
        footer: customComponents?.Footer ? 'Footer' : null,
        ...!isMobile && {
            modifiers,
            modifiersClassNames,
            onDayMouseEnter: handleDayMouseEnter
        }
    };
    const dayPicker = /*#__PURE__*/ jsx(DayPicker, {
        ...dayPickerProps
    });
    return /*#__PURE__*/ jsx(DayMapProvider, {
        dayMap: dayMap,
        mode: mode,
        isMobile: isMobile,
        children: isMobile ? /*#__PURE__*/ jsx("div", {
            ref: monthsContainerRef,
            className: "w-full",
            children: dayPicker
        }) : dayPicker
    });
}
export { Calendar };
